/* eslint-disable react/jsx-no-useless-fragment */
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import classNames from 'classnames';

import { EvButton, EvCard, EvSection, EvSpinner } from '@evinced-private/ui-common';

import { ProductActivationError } from '../../components/product-activation-error/ProductActivationError';
import { ApiErrorHelper } from '../../helpers/ApiErrorHelper';
import { RoutesHelper } from '../../helpers/RoutesHelper';
import { ProductType } from '../../interfaces/Tenant';
import { ITenantDescriptor } from '../../interfaces/TenantDescriptor';
import { logger } from '../../services/Logger';
import {
	activateProduct,
	getActivationErrorType,
	getProductLicenseDetails,
	getTenantDescriptor,
	getUserTenant,
	hasAccessToProduct,
	TenantDescriptorValue,
	TenantPlan
} from '../../services/TenantsService';
import { CommunityPlanInfo } from '../account-plan-info/CommunityPlanInfo';
import { FreeTrialPlanInfo } from '../account-plan-info/FreeTrialPlanInfo';

import './ProductActivationPage.scss';

const activateLicenseButtonText = 'Activate License';
const startTrialButtonText = 'Start Trial';

export const ProductActivationPage: FC = () => {
	const [isLoading, setLoading] = useState(true);
	const [isSaveInProgress, setSaveInProgress] = useState(false);
	const [error, setError] = useState(null);
	const [accountPlan, setAccountPlan] = useState<TenantPlan>(TenantPlan.FREE_TRIAL);
	const { push } = useHistory();

	const goToHomepage = useCallback((): void => {
		push(RoutesHelper.getHomepagePath());
	}, [push]);

	const initPage = useCallback(async (): Promise<void> => {
		try {
			const userHasAccessToProduct = await hasAccessToProduct();
			if (userHasAccessToProduct) {
				goToHomepage();
				return;
			}
			const siteScannerLicense = await getProductLicenseDetails(ProductType.SCANNER);
			if (siteScannerLicense.isExpired) {
				setError('product expired');
				return;
			}
			const tenantDescriptor: ITenantDescriptor = await getTenantDescriptor();
			let accountPlan = null;
			if (tenantDescriptor?.accountPlan?.values.includes(TenantDescriptorValue.COMMUNITY)) {
				accountPlan = TenantPlan.COMMUNITY;
			} else {
				accountPlan = TenantPlan.FREE_TRIAL;
			}
			setAccountPlan(accountPlan);
		} catch (err) {
			logger.error('Error getting user data', err);
		} finally {
			setLoading(false);
		}
	}, [goToHomepage]);

	const isCommunityMode = (): boolean => {
		return accountPlan === TenantPlan.COMMUNITY;
	};

	const getTitleText = (): string => {
		if (isCommunityMode()) {
			return 'Site Scanner Community License';
		}
		return 'Site Scanner 14-Day Free Trial';
	};

	const getButtonText = (): string => {
		return isCommunityMode() ? activateLicenseButtonText : startTrialButtonText;
	};

	useEffect(() => {
		initPage();
	}, [initPage]);

	const activateSiteScanner = async (): Promise<void> => {
		setSaveInProgress(true);
		const tenant = await getUserTenant();
		if (!tenant) {
			logger.error('No tenant was found for this user, weird, they should not go to this page');
			setError('No tenant found');
			setSaveInProgress(false);
			return;
		}
		try {
			await activateProduct(tenant.id);
			logger.debug('Product activated successfully');
			goToHomepage();
		} catch (error) {
			logger.error('Error activating product', error);
			setError(ApiErrorHelper.getErrorMessage(error));
			setSaveInProgress(false);
		}
	};

	if (isLoading) {
		return <EvSpinner />;
	}

	if (error) {
		return <ProductActivationError errorType={getActivationErrorType(error)} />;
	}

	const getStartNowButton = (): JSX.Element => {
		return (
			<div className="start-now-button-wrapper">
				<EvButton onClick={activateSiteScanner} loading={isSaveInProgress}>
					{getButtonText()}
				</EvButton>
			</div>
		);
	};

	return (
		<div className="product-activation-page">
			<EvCard
				title={getTitleText()}
				className={classNames(
					'product-activation-card',
					isCommunityMode() ? 'community-mode' : 'free-trial-mode'
				)}
				footer={getStartNowButton()}
				headingLevel={1}
			>
				<>
					<EvSection ariaLabel="main-section" className="activation-info">
						<div className="creation-information">
							{isCommunityMode() ? <CommunityPlanInfo /> : <FreeTrialPlanInfo />}
						</div>
					</EvSection>
				</>
			</EvCard>
		</div>
	);
};
